import React, { useState, useEffect } from 'react';
import { useCountdown } from '../hooks/useCountdown';

const AnnouncementBanner = ({ 
  onClaimClick, 
  customText, 
  backgroundColor, 
  textColor, 
  enableCloseButton = true,
  bannerType = 'launch', // 'launch' or 'promo'
  expiryDate = null, // ISO date string for countdown
  onExpiry = null // Callback when countdown reaches zero
}) => {
  // Initialize state from localStorage to prevent flash - separate storage for each banner type
  const storageKey = `bannerDismissed_${bannerType}`;
  const [isVisible, setIsVisible] = useState(() => {
    if (!enableCloseButton) return true; // Always show if close button is disabled
    const dismissed = localStorage.getItem(storageKey);
    return dismissed !== 'true';
  });
  const [isAnimating, setIsAnimating] = useState(true);

  // Countdown timer
  const countdown = useCountdown(expiryDate);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(storageKey, 'true');
  };

  // Auto-hide and trigger callback when countdown expires
  useEffect(() => {
    if (countdown && countdown.expired && expiryDate) {
      setIsVisible(false);
      if (onExpiry) {
        onExpiry();
      }
    }
  }, [countdown, expiryDate, onExpiry]);

  if (!isVisible) return null;

  // Default texts based on banner type
  const defaultLaunchText = "🚀 Limited Time Launch Offer: Get 1 Year FREE Premium Access! First 50 users only - 49 spots remaining!";
  const defaultPromoText = "🎉 Special Offer: Enter your promo code and get 50% discount on Yearly Plan!";
  
  const displayText = customText || (bannerType === 'launch' ? defaultLaunchText : defaultPromoText);
  
  // Default colors
  const defaultBg = 'linear-gradient(90deg, #FFF8D1 0%, #FFF4A3 50%, #FFF8D1 100%)';
  const defaultTextColor = '#1A1A1A';
  
  const bgStyle = backgroundColor ? backgroundColor : defaultBg;
  const textColorStyle = textColor || defaultTextColor;

  return (
    <div 
      style={{
        width: '100%',
        background: bgStyle,
        backgroundSize: '200% 100%',
        animation: backgroundColor ? 'none' : (isAnimating ? 'shimmer 3s ease-in-out infinite' : 'none'),
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        borderBottom: '2px solid #FFD700',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 1000
      }}
    >
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 0% 0%; }
            50% { background-position: 100% 0%; }
            100% { background-position: 0% 0%; }
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          
          .claim-button {
            animation: pulse 2s ease-in-out infinite;
          }
          
          @media (max-width: 768px) {
            .banner-content {
              flex-direction: column;
              gap: 12px;
              text-align: center;
            }
            .banner-text {
              font-size: 0.95rem;
            }
          }
        `}
      </style>

      <div 
        className="banner-content"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          maxWidth: '1200px',
          width: '100%',
          justifyContent: 'center'
        }}
      >
        {/* Text */}
        <div 
          className="banner-text"
          style={{
            color: textColorStyle,
            fontSize: '1.05rem',
            fontWeight: '600',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            flex: '1',
            justifyContent: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.5rem' }}>{bannerType === 'launch' ? '🎉' : '🎟️'}</span>
            <span>
              {displayText}
            </span>
          </div>
          
          {/* Countdown Timer - Only show for launch offers with expiry date */}
          {bannerType === 'launch' && countdown && !countdown.expired && countdown.formatted && (
            <div style={{ 
              fontSize: '0.9rem', 
              fontWeight: '500',
              opacity: '0.85',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>⏳</span>
              <span>Offer ends in: {countdown.formatted}</span>
            </div>
          )}
        </div>

        {/* Action Button - Different text based on banner type */}
        <button
          className="claim-button"
          onClick={onClaimClick}
          style={{
            background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 28px',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(255,107,107,0.4)',
            transition: 'all 0.3s ease',
          }}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-2px) scale(1.05)';
            e.target.style.boxShadow = '0 6px 16px rgba(255,107,107,0.6)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 12px rgba(255,107,107,0.4)';
          }}
        >
          {bannerType === 'launch' ? '🚀 Claim Launch Code' : '🎟️ Redeem Discount Code'}
        </button>
      </div>

      {/* Close Button - only show if enabled */}
      {enableCloseButton && (
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: textColorStyle,
            fontSize: '24px',
            cursor: 'pointer',
            padding: '4px 8px',
            lineHeight: '1',
            opacity: '0.6',
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.target.style.opacity = '1'}
          onMouseOut={(e) => e.target.style.opacity = '0.6'}
          aria-label="Dismiss banner"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default AnnouncementBanner;
